import { dateTimeNoSeconds, shortDateTime } from '../../shared/dateTime';
import { plNumber } from '../../shared/number';

/**
 * Migawka rekomendacji z chwili decyzji (makieta back office v2, BR-FTL-16;
 * decyzja użytkownika 20.10.2026; przełącznik `recommendationSnapshot`) —
 * odczyt wpisu dziennika `recommendationSnapshot` (API
 * `user-offers/recommendation-snapshot.ts`). `oldValue` — przy czym
 * (`variant` / `assignment`), `newValue` — JSON migawki. Klucze JSON-a to
 * dane w bazie: czytamy je, nie przemianowujemy.
 *
 * To zapis tego, co lista mówiła w chwili decyzji — nie ocena pojazdu ani
 * aktualna pozycja (ta jest liczona przy otwarciu karty).
 */

export const FIELD_RECOMMENDATION_SNAPSHOT = 'recommendationSnapshot';
export const FIELD_VARIANT_VALID_UNTIL = 'variantValidUntil';

const DECISION_LABELS: Record<string, string> = {
  variant: 'przy dodaniu wariantu',
  assignment: 'przy obsadzeniu',
};

export function snapshotDecisionText(value: string): string {
  return DECISION_LABELS[value] ?? value;
}

interface Verdict {
  willMakeIt: boolean | null;
  reason: string | null;
}

export interface SnapshotRow {
  carId: number;
  plate: string | null;
  rank: number;
  accessKm: number | null;
  accessSource: string | null;
  onTime: Verdict;
  delivery: Verdict;
  warnings: string[];
  busyWith: string | null;
  pairScore?: number | null;
}

export type Snapshot =
  | {
      available: true;
      computedAt: string;
      total: number;
      complete: boolean;
      onlyCaretaker: string | null;
      chosen: (SnapshotRow & { onList: true }) | { onList: false; carId: number; plate: string | null; reason: string };
      top: SnapshotRow[];
    }
  | { available: false; carId: number; reason: string };

/** Migawka z wpisu; `null`, gdy to nie nasz JSON. */
export function parseSnapshot(value: unknown): Snapshot | null {
  if (typeof value !== 'string' || value === '') return null;
  try {
    const v = JSON.parse(value) as { available?: unknown };
    return typeof v.available === 'boolean' ? (v as Snapshot) : null;
  } catch {
    return null;
  }
}

const verdictWord = (v: Verdict | undefined, yes: string, no: string): string =>
  v?.willMakeIt === true ? yes : v?.willMakeIt === false ? no : 'brak danych';

const accessText = (row: SnapshotRow): string =>
  row.accessKm === null
    ? 'dolot —'
    : `dolot ${plNumber(row.accessKm, 0)} km${row.accessSource === 'here' ? '' : ' (szacunek)'}`;

const plateOf = (p: { plate: string | null; carId: number }) => p.plate ?? `#${p.carId}`;

/** Jedno zdanie do kolumny „Po” historii. */
export function snapshotSummary(value: unknown): string {
  const s = parseSnapshot(value);
  if (!s) return typeof value === 'string' ? value : '—';
  if (!s.available) return `brak migawki — ${s.reason}`;
  const c = s.chosen;
  if (!c.onList) return `${plateOf(c)}: poza listą — ${c.reason}`;
  const place = s.complete ? `${c.rank}. z ${s.total}` : `${c.rank}. pozycja (pokazano ${s.total})`;
  return [
    `${plateOf(c)}: ${place} po dolocie`,
    accessText(c),
    verdictWord(c.onTime, 'zdąży', 'nie zdąży'),
    verdictWord(c.delivery, 'dowiezie', 'nie dowiezie'),
    ...(c.warnings.length > 0 ? [`uwagi: ${c.warnings.length}`] : []),
  ].join(' · ');
}

/** Szczegóły do rozwinięcia: kiedy liczono, pierwsze pozycje, uwagi auta. */
export function snapshotDetails(value: unknown): string[] {
  const s = parseSnapshot(value);
  if (!s || !s.available) return [];
  const lines = [
    `Lista policzona ${dateTimeNoSeconds(s.computedAt)}${s.onlyCaretaker ? `, tylko pojazdy opiekuna ${s.onlyCaretaker}` : ''}.`,
    ...s.top.map(
      (r) =>
        `${r.rank}. ${plateOf(r)} — ${accessText(r)}, ${verdictWord(r.onTime, 'zdąży', 'nie zdąży')}, ${verdictWord(r.delivery, 'dowiezie', 'nie dowiezie')}${r.busyWith ? `, zajęty: ${r.busyWith}` : ''}`,
    ),
  ];
  const c = s.chosen;
  if (c.onList) {
    if (c.onTime.reason) lines.push(`Zdąży: ${c.onTime.reason}`);
    if (c.delivery.reason) lines.push(`Dowiezie: ${c.delivery.reason}`);
    for (const w of c.warnings) lines.push(`Uwaga: ${w}`);
  }
  return lines;
}

/** Wpis terminu wariantu: „WGM 7: do 21.10, 17:00”. */
export function validityLogText(value: unknown, vehicles: ReadonlyMap<number, string>): string {
  if (typeof value !== 'string' || value === '') return '—';
  try {
    const v = JSON.parse(value) as { carId?: unknown; validUntil?: unknown };
    if (typeof v.carId !== 'number') return value;
    const plate = vehicles.get(v.carId) ?? `pojazd #${v.carId}`;
    return typeof v.validUntil === 'string'
      ? `${plate}: do ${shortDateTime(v.validUntil)}`
      : `${plate}: bez terminu`;
  } catch {
    return value;
  }
}
